import { Pill } from '@iwoca/orion';
import { Link } from 'react-router';

import { ResultTab } from './deriveResultRows';
import { buildQueryString } from '../../../../Buyer/utils/queryParams';
import { TAB_CONFIGS } from '../../config/tabConfig';
import { companyNameLabel, hasCompanyName } from '../../customerName';
import { CustomerRow } from '../../CustomersV2.types';

const PILL_BY_TAB: Record<
  ResultTab,
  { label: string; variant: 'london' | 'green' | 'red' }
> = {
  'in-progress': { label: TAB_CONFIGS.onboarding.label, variant: 'london' },
  'decision-ready': {
    label: TAB_CONFIGS.spendingLimit.label,
    variant: 'green',
  },
  // `declined` isn't one of the two tabs, so its label is hardcoded — and it
  // has no tab to navigate to, hence the modal branch below.
  declined: { label: 'Declined', variant: 'red' },
};

/** Keyed on the navigable tabs only — a declined result opens a modal instead. */
const TAB_PATH_BY_TAB: Record<Exclude<ResultTab, 'declined'>, string> = {
  'in-progress': TAB_CONFIGS.onboarding.path,
  'decision-ready': TAB_CONFIGS.spendingLimit.path,
};

export const SearchResultRow = ({
  row,
  tab,
  term,
  onNavigate,
  onDeclinedClick,
}: {
  row: CustomerRow;
  tab: ResultTab;
  term: string;
  onNavigate: () => void;
  onDeclinedClick: (row: CustomerRow) => void;
}) => {
  const pill = PILL_BY_TAB[tab];

  const content = (
    <>
      <span
        className={hasCompanyName(row) ? 'font-med' : 'italic text-primary-40'}
      >
        {companyNameLabel(row)}
      </span>
      <Pill
        variant={pill.variant}
        size="small"
        className="shrink-0 whitespace-nowrap !px-[6px] !py-xs !text-xs"
      >
        {pill.label}
      </Pill>
    </>
  );

  // A declined buyer has no row on the destination tab unless "View declined
  // customers" is on, so linking there would strand the seller on an empty
  // table. The modal states the outcome instead.
  if (tab === 'declined') {
    return (
      <button
        type="button"
        onClick={() => onDeclinedClick(row)}
        // Tailwind preflight is off, so the UA button border and text-align have
        // to be reset before the shared row styling reads the same as the links.
        className="flex w-full cursor-pointer items-center justify-between gap-l border-x-0 border-b border-t-0 border-solid border-secondary-95 bg-structure-100 p-r text-left text-m text-primary-10 last:border-b-0 hover:bg-secondary-90"
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={`/pay/customers/${TAB_PATH_BY_TAB[tab]}${buildQueryString({ search: term })}`}
      onClick={onNavigate}
      className="flex items-center justify-between gap-l border-b border-secondary-95 bg-structure-100 p-r text-m text-primary-10 no-underline last:border-b-0 hover:bg-secondary-90"
    >
      {content}
    </Link>
  );
};
